import React from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';

import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';

import type { AmsReconciliationSourceInput } from '@oysterjs/ais/api/graphql';
import { destinationName } from './destinations';
import { ReconciliationPipeline, type DestinationFindings } from './pipeline';
import { ApplyReceipt } from './receipt';
import { ReviewChanges, type DestinationOutcome, type ReviewDraft } from './review-changes';
import { AmsReconciliationStepOne, type StepOneSelection } from './step-one';

export type AmsReconciliationModalProps = {
  open: boolean;
  onClose: () => void;
  /** Where the document text being reconciled comes from — a file or a Cara message. */
  source: AmsReconciliationSourceInput;
  /** Lets step one prefill the account already linked to this workspace's client. */
  workspaceId?: string;
};

/**
 * The steps of a reconciliation run. Each stage carries what it needs to render: review
 * cannot exist without findings, nor the receipt without outcomes.
 */
type Stage =
  | { name: 'select' }
  | { name: 'running'; selection: StepOneSelection }
  | {
      name: 'review';
      selection: StepOneSelection;
      findings: DestinationFindings[];
      // Set when returning from a receipt to retry what was not written.
      outcomes?: DestinationOutcome[];
      draft?: ReviewDraft;
    }
  | { name: 'failed'; selection: StepOneSelection; failures: DestinationFindings[] }
  | {
      name: 'done';
      selection: StepOneSelection;
      findings: DestinationFindings[];
      outcomes: DestinationOutcome[];
      draft: ReviewDraft;
    }
  // A new account has no destinations yet, so creating one ends the flow.
  | { name: 'accountCreated'; accountName: string };

// Only the review grid needs room for four columns; the other steps stay narrow so
// each reads as one thing rather than adrift in a wide frame.
const STAGE_WIDTH: Record<Stage['name'], 'lg' | 'xl' | '5xl'> = {
  select: 'xl',
  running: 'lg',
  failed: 'lg',
  review: '5xl',
  done: 'lg',
  accountCreated: 'lg'
};

const accountNameOf = (selection: StepOneSelection): string =>
  selection.account.kind === 'new'
    ? selection.account.name || 'New account'
    : (selection.account.match.displayName ?? 'Selected account');

// Step one is where the account gets chosen, so only the steps after it can name it.
const titleOf = (stage: Stage): string => {
  switch (stage.name) {
    case 'select':
      return 'Reconcile with AMS';
    case 'done':
      return 'Reconciled';
    case 'accountCreated':
      return 'Account created';
    default:
      return `Reconcile with ${accountNameOf(stage.selection)}`;
  }
};

export function AmsReconciliationModal(props: AmsReconciliationModalProps) {
  const [stage, setStage] = React.useState<Stage>({ name: 'select' });
  const [applying, setApplying] = React.useState(false);
  const [pendingLeave, setPendingLeave] = React.useState<(() => void) | null>(null);

  // Reset on reopen so a prior run doesn't linger behind a fresh source.
  React.useEffect(() => {
    if (props.open) {
      setStage({ name: 'select' });
    }
    setPendingLeave(null);
    setApplying(false);
  }, [props.open]);

  // Every stage before the receipt holds work that leaving would throw away - a chosen
  // account, an extraction in flight, unwritten edits - so leaving asks first. An apply
  // already sent keeps writing after the modal is gone, and the receipt of what landed
  // would go with it, so nothing can close then.
  const requestLeave = (proceed: () => void) => {
    if (stage.name === 'review' && applying) {
      return;
    }
    if (stage.name === 'select' || stage.name === 'running' || stage.name === 'review') {
      setPendingLeave(() => proceed);
      return;
    }
    proceed();
  };

  // A retry returns to review carrying the first apply's outcomes, some of which landed.
  const partlyWritten =
    stage.name === 'review' && stage.outcomes?.some((outcome) => outcome.status !== 'refused');

  const confirmLeave = () => {
    const proceed = pendingLeave;
    setPendingLeave(null);
    proceed?.();
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={() => requestLeave(props.onClose)}
        size={STAGE_WIDTH[stage.name]}
      >
        <div className="flex items-start justify-between gap-4">
          <DialogTitle>{titleOf(stage)}</DialogTitle>
          <Button
            type="button"
            plain
            aria-label="Close"
            className="flex-none"
            disabled={stage.name === 'review' && applying}
            onClick={() => requestLeave(props.onClose)}
          >
            <XMarkIcon data-slot="icon" />
          </Button>
        </div>
        <DialogBody className="mt-4">
          {stage.name === 'select' && (
            <AmsReconciliationStepOne
              source={props.source}
              workspaceId={props.workspaceId}
              onCancel={() => requestLeave(props.onClose)}
              onContinue={(selection) => setStage({ name: 'running', selection })}
              onAccountCreated={(accountName) => setStage({ name: 'accountCreated', accountName })}
            />
          )}

          {stage.name === 'running' && (
            <ReconciliationPipeline
              source={props.source}
              targets={stage.selection.destinations}
              onComplete={(findings) =>
                setStage({ name: 'review', selection: stage.selection, findings })
              }
              onError={(failures) =>
                setStage({ name: 'failed', selection: stage.selection, failures })
              }
            />
          )}

          {stage.name === 'failed' && (
            <div className="flex flex-col gap-3">
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                <p className="m-0">
                  Couldn&rsquo;t reconcile against the AMS. Nothing was written.
                </p>
                <ul className="m-0 mt-2 list-disc pl-5">
                  {stage.failures.map((failure) => (
                    <li key={failure.target.id}>
                      {destinationName(failure.target)}: {failure.failure}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => requestLeave(() => setStage({ name: 'select' }))}
                className="self-start text-xs font-medium text-sky-700 hover:underline dark:text-sky-300"
              >
                Back to destinations
              </button>
            </div>
          )}

          {stage.name === 'review' && (
            <ReviewChanges
              accountName={accountNameOf(stage.selection)}
              findings={stage.findings}
              outcomes={stage.outcomes}
              draft={stage.draft}
              onApplyingChange={setApplying}
              onBack={() => requestLeave(() => setStage({ name: 'select' }))}
              onApplied={(outcomes, draft) =>
                setStage({
                  name: 'done',
                  selection: stage.selection,
                  findings: stage.findings,
                  outcomes,
                  draft
                })
              }
            />
          )}

          {stage.name === 'done' && (
            <ApplyReceipt
              accountName={accountNameOf(stage.selection)}
              outcomes={stage.outcomes}
              onRetry={() =>
                setStage({
                  name: 'review',
                  selection: stage.selection,
                  findings: stage.findings,
                  outcomes: stage.outcomes,
                  draft: stage.draft
                })
              }
              onDone={() => requestLeave(props.onClose)}
            />
          )}

          {stage.name === 'accountCreated' && (
            <div className="flex flex-col gap-6">
              <div>
                <p className="m-0 text-lg/6 font-semibold text-neutral-950 dark:text-white">
                  {stage.accountName} created in Applied Epic
                </p>
                <p className="m-0 mt-2 text-sm/6 text-neutral-500 dark:text-neutral-400">
                  It has no policies yet, so there is nothing to reconcile against until one exists
                  on the account.
                </p>
              </div>
              <div className="flex justify-end">
                <Button type="button" color="sky" onClick={() => requestLeave(props.onClose)}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogBody>
      </Dialog>
      <Dialog open={pendingLeave != null} onClose={() => setPendingLeave(null)} size="md">
        <DialogTitle>
          {partlyWritten ? 'Stop retrying?' : 'Discard this reconciliation?'}
        </DialogTitle>
        <DialogDescription>
          {partlyWritten
            ? "Changes already written to the AMS stay there. The rest won't be applied."
            : 'Nothing has been written to the AMS.'}
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setPendingLeave(null)}>
            Keep going
          </Button>
          <Button color="red" onClick={confirmLeave}>
            {partlyWritten ? 'Stop' : 'Discard'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
